import { useParams } from "react-router-dom";
import { GlobalStyle } from "../../common/styles/global-style"
import { ControlSoundButtons, ControlSoundCorner, EstimulusColorBox, SoundToggleButton } from "./style"
import { ArrowStimulus } from "./arrow-stimulus";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRealtimeChannel } from "../../common/services/realtime/realtime-hook";
import { RealtimeActions } from "../../common/services/realtime/realtime-actions";
import { StimulusType, parseArrowDirection, ARROW_HIDE_COMMAND } from "../../common/services/realtime/stimulus-types";
import { IoVolumeHigh, IoVolumeOff } from "react-icons/io5";

const ACTION_COLOR_PREFIX = `${StimulusType.ACTION_COLOR}_`;

const isStimulusMessage = (msg: string): boolean =>
    msg.startsWith(StimulusType.ACTION_COLOR) || parseArrowDirection(msg) !== undefined;

const isDisplayMessage = (msg: string): boolean =>
    isStimulusMessage(msg) || msg === ARROW_HIDE_COMMAND;

export const MonitorUi = () => {
    const { sessionId } = useParams<{ sessionId: string }>();
    const { messages, publish } = useRealtimeChannel<string>(sessionId ?? "");
    const wakeLockRef = useRef<WakeLockSentinel | null>(null);
    const [beepEnabled, setBeepEnabled] = useState(false);
    const actionColorCountRef = useRef(0);

    const lastStimulus = useMemo(() => {
        const stimuli = messages.filter(isDisplayMessage);
        return stimuli[stimuli.length - 1];
    }, [messages]);

    const arrowsHidden = lastStimulus === ARROW_HIDE_COMMAND;

    const displayColor = lastStimulus?.startsWith(ACTION_COLOR_PREFIX)
        ? lastStimulus.slice(ACTION_COLOR_PREFIX.length)
        : undefined;
    const displayArrow = lastStimulus ? parseArrowDirection(lastStimulus) : undefined;

    useEffect(() => {
        const actionColorCount = messages.filter(isStimulusMessage).length;
        if (actionColorCount > actionColorCountRef.current && beepEnabled) {
            const audio = new Audio("/assets/beep/1.mp3");
            audio.play().catch(() => { });
        }
        actionColorCountRef.current = actionColorCount;
    }, [messages, beepEnabled]);

    useEffect(() => {
        const publishRealtime = async () => {
            await publish(RealtimeActions.SESSION_SYN_ACK);
        }
        publishRealtime();
    }, [sessionId]);

    useEffect(() => {
        if (!navigator.wakeLock) return;

        const requestWakeLock = () => {
            if (document.visibilityState !== "visible") return;
            navigator.wakeLock
                .request("screen")
                .then((sentinel) => {
                    wakeLockRef.current = sentinel;
                })
                .catch(() => { });
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                if (wakeLockRef.current) {
                    wakeLockRef.current.release().catch(() => { });
                    wakeLockRef.current = null;
                }
            } else {
                requestWakeLock();
            }
        };

        const handleUserGesture = () => {
            if (document.visibilityState === "visible" && !wakeLockRef.current) {
                requestWakeLock();
            }
        };

        requestWakeLock();
        document.addEventListener("visibilitychange", handleVisibilityChange);
        document.addEventListener("touchstart", handleUserGesture, { passive: true });
        document.addEventListener("click", handleUserGesture);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            document.removeEventListener("touchstart", handleUserGesture);
            document.removeEventListener("click", handleUserGesture);
            if (wakeLockRef.current) {
                wakeLockRef.current.release().catch(() => { });
                wakeLockRef.current = null;
            }
        };
    }, []);

    const showAtencao = lastStimulus === undefined;
    const showBeepControl = showAtencao;

    const soundButton = (
        <SoundToggleButton
            type="button"
            onClick={() => setBeepEnabled((v) => !v)}
            aria-label={beepEnabled ? "Desligar alerta sonoro" : "Ativar alerta sonoro"}
            title={beepEnabled ? "Desligar alerta sonoro" : "Ativar alerta sonoro"}
        >
            {beepEnabled ? (
                <IoVolumeHigh size={40} aria-hidden />
            ) : (
                <IoVolumeOff size={40} aria-hidden />
            )}
        </SoundToggleButton>
    );

    return (
        <>
            <GlobalStyle />
            {showBeepControl ? <ControlSoundButtons>{soundButton}</ControlSoundButtons> : null}
            {displayArrow || arrowsHidden ? <ControlSoundCorner>{soundButton}</ControlSoundCorner> : null}
            {displayArrow
                ? <ArrowStimulus direction={displayArrow} />
                : (
                    <EstimulusColorBox color={displayColor}>
                        {showAtencao ? "Atenção!" : null}
                    </EstimulusColorBox>
                )}
        </>
    )
}
